import { PCFShadowMap } from "three";
import type { LightKind } from "../modeling/types.ts";

export interface RealtimeShadowTuning {
  bias: number;
  normalBias: number;
  radius: number;
}

export interface RealtimeShadowCamera {
  left: number;
  right: number;
  top: number;
  bottom: number;
  near: number;
  far: number;
}

export interface RealtimeLightShadowSettings extends RealtimeShadowTuning {
  castShadow: boolean;
  mapSize: [number, number];
  resourceKey: string;
  camera?: RealtimeShadowCamera;
}

const MIN_SHADOW_SOFTNESS = 1;
const MAX_SHADOW_SOFTNESS = 100;
const REFERENCE_SHADOW_MAP_SIZE = 1024;
const MIN_PCF_RADIUS = 0.35;
const MAX_PCF_RADIUS = 18;

export const REALTIME_SHADOW_MAP_TYPE = PCFShadowMap;
export const REALTIME_CANVAS_SHADOWS = {
  type: REALTIME_SHADOW_MAP_TYPE,
} as const;

export const DIRECTIONAL_REALTIME_SHADOW_CAMERA: RealtimeShadowCamera = {
  left: -5,
  right: 5,
  top: 5,
  bottom: -5,
  near: 0.1,
  far: 30,
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function shadowBiasForKind(
  kind: LightKind,
): Pick<RealtimeShadowTuning, "bias" | "normalBias"> {
  switch (kind) {
    case "directional_light":
      return {
        bias: 0,
        normalBias: 0.02,
      };
    case "point_light":
      return {
        bias: 0,
        normalBias: 0.04,
      };
  }
}

export function resolveRealtimeShadowTuning(
  kind: LightKind,
  softness: number,
  mapSize: number,
): RealtimeShadowTuning {
  const clampedSoftness = clamp(
    softness,
    MIN_SHADOW_SOFTNESS,
    MAX_SHADOW_SOFTNESS,
  );
  const softnessT =
    (clampedSoftness - MIN_SHADOW_SOFTNESS) /
    (MAX_SHADOW_SOFTNESS - MIN_SHADOW_SOFTNESS);
  const pcfRadiusAtReferenceSize = lerp(
    MIN_PCF_RADIUS,
    MAX_PCF_RADIUS,
    softnessT,
  );

  return {
    ...shadowBiasForKind(kind),
    radius: pcfRadiusAtReferenceSize * (mapSize / REFERENCE_SHADOW_MAP_SIZE),
  };
}

export function resolveRealtimeLightShadowSettings(
  kind: LightKind,
  enabled: boolean,
  softness: number,
  mapSize: number,
): RealtimeLightShadowSettings {
  const tuning = resolveRealtimeShadowTuning(kind, softness, mapSize);

  return {
    castShadow: enabled,
    mapSize: [mapSize, mapSize],
    resourceKey: `${kind}:${REALTIME_SHADOW_MAP_TYPE}:${mapSize}`,
    camera:
      kind === "directional_light"
        ? DIRECTIONAL_REALTIME_SHADOW_CAMERA
        : undefined,
    ...tuning,
  };
}
